/**
 * The card view of one activation.
 *
 * The table is the default because everything about an activation is comparative — you scan down
 * Status, down Schedule. The card earns its place on one thing: the changed → synced pair, which in the
 * table is a stacked sub-line in a 12.5rem cell and here gets a footer of its own.
 *
 * Composed over the shared `ListCard` (stretched link + actions slot) rather than hand-rolled, so it
 * carries the same hover/focus treatment as every other card grid in the app.
 *
 * The card is HEADED by the same `ActivationStrip` the table row is identified by — the two are views
 * of one list and must be recognisably the same object in both. `ListCard` takes it through
 * `titleContent`, an additive slot on the shared primitive rather than a fork of it: `title` stays the
 * string the card is sorted, searched and (by default) named by, while the header row renders the
 * relation. The audience therefore left the meta line, where it used to sit as a lone `Users` chip —
 * it is half the identifier, and stating it twice on one card is the thing this surface refuses to do
 * everywhere else.
 */
import { Pencil, Trash2 } from 'lucide-react';

import { KebabMenu } from '@/_shared/components/list/KebabMenu';
import { ListCard } from '@/_shared/components/list/ListCard';

import {
  activationRowLabel,
  destinationAudienceLabel,
  destinationSubLabel,
  hasResolvedDestinationName,
  relativeTime,
  syncedOfChanged,
} from '../lib/activation-list';
import { isRunning } from '../lib/run-view';
import type { ActivationRow } from '../types';
import { ActivationStrip } from './ActivationStrip';
import { ScheduleLabel } from './ScheduleLabel';
import { SyncStatusBadge } from './SyncStatusBadge';

interface ActivationCardProps {
  row: ActivationRow;
  /** The audience's resolved name — the row itself only ever carries an id. */
  audienceName: string;
  /** Built with `wp()` by the page; the whole card navigates here. */
  to: string;
  /** Omitted where the card is read-only; the ⋯ menu is then not rendered at all. */
  onDelete?: (row: ActivationRow) => void;
  /** Same, and same menu as the table row — see the note on `actions` below. */
  onEdit?: (row: ActivationRow) => void;
  /**
   * Why `Edit` is unavailable, or `undefined` when it is not. See `ActivationsList.tsx`.
   *
   * A REASON, not a `demo` boolean: the boolean folded the still-unknown window into "demo", so a real
   * workspace was told "not available in the demo workspace" while `workspace.get` was in flight.
   *
   * Required, not optional: `ActivationsTable` already requires it, and a defaulted `false` here
   * would let a future caller pass `onEdit` without it and ship a live Edit control in a demo
   * workspace behind a green typecheck.
   */
  editDisabledReason: string | undefined;
  /**
   * Why `Delete` is unavailable, or `undefined` when it is not: a disabled or gone sibling turns activation
   * actions off, with the banner's text as the reason (ACT2-1124 LLD §5.9). Required for the same reason as above.
   */
  deleteDisabledReason: string | undefined;
}

export function ActivationCard({
  row,
  audienceName,
  to,
  onDelete,
  onEdit,
  editDisabledReason,
  deleteDisabledReason,
}: ActivationCardProps): React.ReactElement {
  const title = destinationAudienceLabel(row);
  const subLabel = destinationSubLabel(row);
  const run = row.lastRun;
  // A live run shows no ratio: its numbers are a moving partial total, and this footer must never state
  // a proportion that is not yet true.
  const summary = run && !isRunning(run) ? syncedOfChanged(run) : null;
  const updatedAgo = relativeTime(row.updatedAt);

  return (
    <ListCard
      to={to}
      // Same ⋯ menu the table row carries, in the slot `ListCard` reserves above the stretched link —
      // the two views of one list must offer the same actions, or the toggle becomes a capability
      // switch rather than a rendering choice.
      actions={
        onDelete || onEdit ? (
          <KebabMenu
            label={`Actions for ${title}`}
            // Edit above Delete, in the table's order — the reversible action first, the destructive
            // one at the bottom edge. Edit shipped on the table row and not here, so the view toggle
            // silently became the capability switch the note above says it must not be.
            actions={[
              ...(onEdit
                ? [
                    {
                      label: 'Edit',
                      icon: Pencil,
                      disabled: editDisabledReason !== undefined,
                      disabledReason: editDisabledReason ?? '',
                      onSelect: () => onEdit(row),
                    },
                  ]
                : []),
              ...(onDelete
                ? [
                    {
                      label: 'Delete',
                      icon: Trash2,
                      destructive: true,
                      disabled: deleteDisabledReason !== undefined,
                      disabledReason: deleteDisabledReason ?? '',
                      onSelect: () => onDelete(row),
                    },
                  ]
                : []),
            ]}
          />
        ) : undefined
      }
      // Still the card's string identity — what `ListCard` puts in its default accessible name, and
      // what a caller would sort by. The header row renders the relation instead.
      title={title}
      titleContent={
        <ActivationStrip
          size="md"
          // `flex-1` was already here and now earns a second job: with the `Disabled` badge retired it
          // is the header row's only child, so it fills the card.
          className="flex-1"
          // `flow`, NOT `tracks` — the one surface that deliberately diverges from the table.
          //
          // Tracks were tried here first, on the reasoning that equal-width cards would line the arrows
          // up down each column of the grid, free. Measured in a browser, they are not free. The 3:2
          // ratio was benched against the TABLE's strip, which was wider than a card's — a 2fr
          // reservation cleared every real far-side label there and did not on a card, where
          // `sql-audience sink pg` rendered as `sql-audience…` while rendering whole under `flow`.
          //
          // The pixel figures that used to sit in this paragraph were measured against a table column
          // that has since narrowed (31.5rem → 29rem, to pay for the Result column), so they are gone
          // rather than quietly wrong. The CONCLUSION is unaffected: a card's strip is narrower than the
          // table's, and it was already the narrower case that broke.
          //
          // That is the far side, which is the DISCRIMINATOR: the only field separating two syncs into
          // the same ad account. Never clipping it is the constraint the 3:2 ratio was chosen to satisfy
          // in the first place, so a width where tracks break it is a width where tracks do not belong.
          //
          // The cost is real and accepted: no arrow column down the grid. A 3-up grid is scanned as
          // tiles rather than as a column of rows, and there is no adjacent arrow to read against, so
          // the alignment buys far less here than it does in the table — and it is not worth the field
          // that tells two activations apart. Fix the ratio per-surface only if a card ever needs it.
          layout="flow"
          enabled={row.enabled}
          audienceLabel={audienceName}
          warehouseType={row.warehouseType}
          destinationLabel={title}
          destinationDefinitionName={row.destination?.definitionName}
          // Same rule as the table's identity column: an unresolved far side is an identifier, not a name.
          destinationIsIdentifier={!hasResolvedDestinationName(row)}
        />
      }
      // The whole relation, worded exactly as the table row's activator — the two views name the same
      // object the same way. This is ALSO the only way a disabled sync reaches a screen reader here:
      // an `aria-label` on the link replaces its contents, so the strip's dot and its own `sr-only`
      // connective are both silent inside it. `activationRowLabel` carries the state for exactly that
      // reason; drop it there and this card announces nothing about it.
      ariaLabel={activationRowLabel(row, audienceName)}
      // No `badges` at all. The `Disabled` one went with the table's Status column and for the same
      // reason: the strip in the header now draws the state as its pilot light, and a badge beside it
      // states one fact twice on one card — the thing this surface refuses to do everywhere else. Its
      // absence is also what leaves the strip alone in the header row, which is what lets the grid
      // template have the full card width to divide.
      meta={
        <>
          {/* The destination the far side LIVES IN — its name only where a person chose it, the
              platform otherwise (`destinationSubLabel`). No glyph of its own: the strip in the header
              already carries the platform's brand mark, and a second copy of it two lines down says
              nothing the first did not — the same call the table's sub-line makes. */}
          <span title={subLabel} className="min-w-0 truncate">
            {subLabel}
          </span>
          {/* The same `ScheduleLabel` the table cell uses, in its full-sentence form because a card has
              the room. A disabled sync used to render a bare em dash here; the component states the
              cadence muted instead, and its docblock has the reasoning. */}
          {/* Muted either way here, overriding the component's enabled/disabled tone: a card's meta line
              is uniformly secondary, and letting the cadence come out darker than the sub-label beside
              it made it the loudest thing on the row. The table keeps the tone difference, where the
              column is scanned and a paused row should read as paused. */}
          <ScheduleLabel schedule={row.schedule} enabled={row.enabled} className="text-muted-foreground" />
        </>
      }
      footerLeft={
        run ? (
          <>
            <SyncStatusBadge run={run} bordered />
            {summary !== null && (
              <span title={summary} className="min-w-0 truncate">
                {summary}
              </span>
            )}
          </>
        ) : (
          <span>No runs yet</span>
        )
      }
      footerRight={updatedAgo === null ? undefined : `Updated ${updatedAgo}`}
    />
  );
}
